import { useEffect, useRef } from "react";
import * as signalR from "@microsoft/signalr";
import { store } from "../index";
import ApiHelper from "../Utils/API/apiHelper";

/**
 * Connects to the FlowBox SignalR hub and notifies the caller when a device's
 * status changes. Joins one group per company ID so notifications are scoped.
 *
 * @param {number[]} companyIds - Company IDs the current user belongs to.
 * @param {(deviceId: number, statusId: number) => void} onDeviceStatusChanged
 */
export function useFlowBoxHub(companyIds, onDeviceStatusChanged) {
  const callbackRef = useRef(onDeviceStatusChanged);
  useEffect(() => { callbackRef.current = onDeviceStatusChanged; }, [onDeviceStatusChanged]);

  const companyKey = [...new Set(companyIds)].sort().join(",");

  useEffect(() => {
    if (!companyKey) return;

    const apiBase = ApiHelper.appSettings?.TAPLR_API_BASE_URL;
    if (!apiBase) return;

    let hubUrl;
    try {
      hubUrl = new URL(apiBase).origin + "/hubs/flowbox";
    } catch {
      return;
    }

    let stopped = false;

    const connection = new signalR.HubConnectionBuilder()
      .withUrl(hubUrl, {
        accessTokenFactory: () => store.getState().Login.userToken,
        // JWT is sent via accessTokenFactory, not cookies — disabling credential
        // mode so the server's wildcard CORS policy is accepted by the browser.
        withCredentials: false,
      })
      .withAutomaticReconnect()
      .configureLogging(signalR.LogLevel.Warning)
      .build();

    connection.on("DeviceStatusChanged", (deviceId, statusId) => {
      console.log("[FlowBoxHub] DeviceStatusChanged", { deviceId, statusId });
      callbackRef.current?.(deviceId, statusId);
    });

    const uniqueIds = [...new Set(companyIds)];
    connection
      .start()
      .then(() => {
        if (stopped) return;
        console.log("[FlowBoxHub] Connected, joining companies:", uniqueIds);
        return Promise.all(uniqueIds.map((id) => connection.invoke("JoinCompany", id)));
      })
      .then(() => { if (!stopped) console.log("[FlowBoxHub] Joined company groups"); })
      .catch((err) => {
        // AbortError is expected when cleanup runs before start() resolves
        // (React StrictMode double-invoke, page navigation). Suppress it.
        if (err?.name !== "AbortError") {
          console.warn("[FlowBoxHub] Connection/join failed:", err);
        }
      });

    return () => {
      stopped = true;
      connection.stop().catch(() => {}); // stop() returns a Promise - must be caught
    };
  }, [companyKey]); // eslint-disable-line react-hooks/exhaustive-deps
}
